import {
    Box,
    FormControl,
    FormLabel,
    Stack,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { Field } from 'formik';
import { useState, useMemo } from 'react';

import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    dropDownFormStyle,
    formInputStyles,
    formLabelStyling,
    formSelectStyles,
    stackSpacing,
    stackStyling,
    textBoxStyling,
} from 'components/forms/new/formStyles';
import { Input } from 'components/v4';

const RegistrationDurationField = ({ values, darkMode, touched }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [showInput, setShowInput] = useState(false);
    const [selectedValue, setSelectedValue] = useState(
        values.registrationDuration?.toString() || '3600',
    );

    const baseStyles = dropDownFormStyle(darkMode);

    const registrationDurationOptions = useMemo(() => {
        const baseOptions = [
            { value: '0', label: '0' },
            { value: '1800', label: '1800' },
            { value: '3600', label: '3600' },
        ];

        if (
            selectedValue &&
            !baseOptions.find((opt) => opt.value === selectedValue)
        ) {
            const allOptions = [
                ...baseOptions,
                { value: selectedValue, label: selectedValue },
            ]
                .map((opt) => ({ ...opt, numValue: Number(opt.value) }))
                .sort((a, b) => a.numValue - b.numValue)
                .map((opt) => ({ value: opt.value, label: opt.label }));
            return [...allOptions, { value: 'Custom', label: 'Custom' }];
        }

        return [...baseOptions, { value: 'Custom', label: 'Custom' }];
    }, [selectedValue]);

    const handleChange = (selectedOption, form) => {
        if (selectedOption?.value === 'Custom') {
            setShowInput(true);
        } else {
            setShowInput(false);
            const value = selectedOption?.value || '3600';
            setSelectedValue(value);
            form.setFieldValue('registrationDuration', value);
        }
    };

    const selectedOption = useMemo(() => {
        return (
            registrationDurationOptions.find(
                (opt) => opt.value === selectedValue,
            ) || null
        );
    }, [registrationDurationOptions, selectedValue]);

    return (
        <FormControl
            sx={{ margin: '0 !important' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={{ ...stackStyling(), ...formInputStyles }}>
                <FormLabel sx={formLabelStyling(darkMode)}>
                    Registration Duration
                    <Text as="span" fontSize="sm" color="gray.500" ml={1}>
                        (seconds)
                    </Text>
                </FormLabel>
                <Field name="registrationDuration">
                    {({ field, form: { setFieldValue, setTouched } }) => (
                        <Box
                            flex="1"
                            sx={{
                                paddingTop: '15px',
                                ...formSelectStyles,
                            }}>
                            <VStack width="100%" alignItems="normal">
                                {!showInput ? (
                                    <Select
                                        {...field}
                                        tabSelectsValue
                                        openMenuOnFocus
                                        placeholder="Select duration"
                                        sx={{
                                            ...textBoxStyling,
                                            color: darkMode
                                                ? 'dark.textDark'
                                                : null,
                                            background: darkMode
                                                ? 'dark.bgDark'
                                                : null,
                                            borderColor: darkMode
                                                ? 'dark.tcap-borderGray'
                                                : null,
                                        }}
                                        value={selectedOption}
                                        options={registrationDurationOptions}
                                        getOptionLabel={(option) =>
                                            option.label
                                        }
                                        getOptionValue={(option) =>
                                            option.value
                                        }
                                        chakraStyles={baseStyles}
                                        onChange={(selectedOption) =>
                                            handleChange(selectedOption, {
                                                setFieldValue,
                                            })
                                        }
                                        onBlur={() => {
                                            setTouched({
                                                ...touched,
                                                registrationDuration: true,
                                            });
                                        }}
                                        selectedOptionColor="brand"
                                    />
                                ) : (
                                    <Input
                                        {...field}
                                        type="number"
                                        placeholder="Enter duration in seconds (Eg. 3600)"
                                        className="!h-[30px] rounded-[5px] pl-2"
                                        min="1"
                                        step="1"
                                        onWheel={(e) => e.target.blur()}
                                        onBlur={(e) => {
                                            const value = e.target.value;
                                            if (
                                                value &&
                                                value !== selectedValue
                                            ) {
                                                setSelectedValue(value);
                                                setFieldValue(
                                                    'registrationDuration',
                                                    value,
                                                );
                                            }
                                            setShowInput(false);
                                        }}
                                    />
                                )}
                            </VStack>
                        </Box>
                    )}
                </Field>
                {isHovered && (
                    <FieldToolTip
                        label="The registration expiry time in seconds. This value is sent in the Expires header of the SIP REGISTER request. Your system will attempt to re-register before this time elapses. A common value is 3600 (1 hour)."
                        ariaLabel="registration-duration-tip"
                        left="25px"
                        top="25px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default RegistrationDurationField;
